import React, { useState, useEffect, useCallback } from 'react'
import { DollarSign, Package, BarChart3, Layers, Loader2, ChevronLeft, ChevronRight, Search, Tag, X, Eye, Heart, MessageCircle, Share2, Merge, ArrowLeft, Info } from 'lucide-react'
import { KPICard } from '../ui'
import useGlobalCustomerStore from '../../stores/globalCustomerStore'
import useSocialMediaStore from '../../stores/socialMediaStore'
import scoutService, { OwnedBrand } from '../../services/scoutService'
import CategoryDetailView from './CategoryDetailView'
import MultiSelectDropdown, { MSOption } from './MultiSelectDropdown'
import BrandCurationPanel from '../scout/BrandCurationPanel'

const DETECTION_TYPE_OPTS: MSOption[] = [
  { value: 'visual_logo', label: 'Visual Logo' },
  { value: 'caption_mention', label: 'Caption Mention' },
  { value: 'visual_text_overlay', label: 'Text Overlay' },
  { value: 'visual_product', label: 'Product' },
  { value: 'hashtag', label: 'Hashtag' },
  { value: 'text_mention', label: 'Text Mention' },
]
const PLACEMENT_OPTS: MSOption[] = [
  { value: 'primary', label: 'Primary' },
  { value: 'secondary', label: 'Secondary' },
  { value: 'product_placement', label: 'Product Placement' },
  { value: 'text_mention', label: 'Text Mention' },
  { value: 'background', label: 'Background' },
]
const CATEGORY_OPTS: MSOption[] = [
  'highlight', 'game_day_announcement', 'in_game_update', 'final_result_recap', 'player_spotlight',
  'training_practice', 'fan_engagement', 'hype_promo', 'ticketing_merch', 'behind_the_scenes',
  'news_announcement', 'community_charity', 'other',
].map((v) => ({ value: v, label: v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) }))

const PAGE_SIZE = 32
const fmtEMV = (n: number): string =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M` : n >= 1_000 ? `$${(n / 1_000).toFixed(2)}K` : `$${(n || 0).toFixed(2)}`
const fmtNum = (n: number): string => (n >= 1000 ? `${(n / 1000).toFixed(1)}K` : `${n ?? 0}`)

// Per-brand color (deterministic by name) — gives each card a distinct accent + initial avatar
// so the grid isn't white-on-white. Class strings are literal so Tailwind keeps them.
const PALETTE: { avatar: string; bar: string }[] = [
  { avatar: 'bg-blue-100 text-blue-700', bar: 'bg-blue-500' },
  { avatar: 'bg-emerald-100 text-emerald-700', bar: 'bg-emerald-500' },
  { avatar: 'bg-amber-100 text-amber-700', bar: 'bg-amber-500' },
  { avatar: 'bg-purple-100 text-purple-700', bar: 'bg-purple-500' },
  { avatar: 'bg-rose-100 text-rose-700', bar: 'bg-rose-500' },
  { avatar: 'bg-cyan-100 text-cyan-700', bar: 'bg-cyan-500' },
  { avatar: 'bg-indigo-100 text-indigo-700', bar: 'bg-indigo-500' },
  { avatar: 'bg-teal-100 text-teal-700', bar: 'bg-teal-500' },
  { avatar: 'bg-orange-100 text-orange-700', bar: 'bg-orange-500' },
  { avatar: 'bg-pink-100 text-pink-700', bar: 'bg-pink-500' },
]
const brandStyle = (name: string) => PALETTE[[...(name || '?')].reduce((a, c) => a + c.charCodeAt(0), 0) % PALETTE.length]

const SORTS: { key: string; label: string }[] = [
  { key: 'total_emv', label: 'EMV' },
  { key: 'posts', label: 'Posts' },
  { key: 'brand_name', label: 'Name (A–Z)' },
]

/**
 * Brand Value — brands detected in the customer's OWNED/PARTNER content, ranked by EMV. A
 * dashboard-side mirror of the Brand Scout brands page (which is competitor-only), sharing the
 * Value Map's inherited filters (period / owned-partner accountType / platform / account) so the
 * two align. No heat (heat is competitor-only).
 */
const BrandValuePanel: React.FC = () => {
  const { selectedCustomer, getAPIFilterParams } = useGlobalCustomerStore()
  const customerId = selectedCustomer?.id ? String(selectedCustomer.id) : ''
  // Curation opens in place rather than as a separate route: the brands being merged are the
  // ones on screen, and returning reloads so the list reflects what just changed.
  const [curating, setCurating] = useState(false)
  // Own brands are out of the totals by default: a customer's own brand is detected in their
  // own content and would otherwise dominate their own "brand value".
  const [includeSelf, setIncludeSelf] = useState(false)
  const { currentPeriod, customDateRange, accountType, selectedPlatformIds, selectedAccountIds } = useSocialMediaStore()

  const [brands, setBrands] = useState<OwnedBrand[]>([])
  const [summary, setSummary] = useState<{ unique_brands: number; total_posts: number; total_emv: number; avg_emv: number } | null>(null)
  const [totalPages, setTotalPages] = useState(1)
  const [page, setPage] = useState(1)
  const [sortBy, setSortBy] = useState('total_emv')
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [detailBrand, setDetailBrand] = useState<OwnedBrand | null>(null)
  // Drill-down filters (mirror the Brand Scout brand-posts page): detection type, placement, category.
  const [detectionTypes, setDetectionTypes] = useState<string[]>([])
  const [placementTiers, setPlacementTiers] = useState<string[]>([])
  const [contentCategories, setContentCategories] = useState<string[]>([])
  const openBrand = (b: OwnedBrand) => { setDetectionTypes([]); setPlacementTiers([]); setContentCategories([]); setDetailBrand(b) }

  const isCustom = currentPeriod === 'custom'
  const globalAccountIds = getAPIFilterParams().accountIds
  const accountIds = globalAccountIds && globalAccountIds.length ? globalAccountIds
    : selectedAccountIds.length ? selectedAccountIds : undefined
  const platformIds = selectedPlatformIds.length ? selectedPlatformIds : undefined
  // Inherited filters shared by the brand list and the per-brand drill-down.
  const filterOpts = {
    accountType, accountIds, platformIds,
    period: isCustom ? undefined : currentPeriod,
    customStartDate: isCustom && customDateRange ? customDateRange.start : undefined,
    customEndDate: isCustom && customDateRange ? customDateRange.end : undefined,
  }

  const load = useCallback(async () => {
    if (!customerId) return
    setLoading(true); setError(null)
    try {
      const res = await scoutService.getOwnedBrands(customerId, {
        includeSelf,
        page, limit: PAGE_SIZE, sortBy, order: sortBy === 'brand_name' ? 'asc' : 'desc',
        search: search.trim() || undefined,
        ...filterOpts,
      })
      setBrands(res?.data ?? [])
      setSummary(res?.summary ?? null)
      setTotalPages(res?.pagination?.totalPages ?? 1)
    } catch (e) {
      // Surface the server's message (BaseService throws with error.message) rather than a
      // generic string — a feature-gate 403 reads "… is not enabled for your organization",
      // which is far more actionable than "Failed to load".
      const msg = e instanceof Error && e.message ? e.message : 'Failed to load brand value'
      setError(msg); setBrands([]); setSummary(null)
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, page, sortBy, search, currentPeriod, customDateRange?.start, customDateRange?.end, accountType, includeSelf, JSON.stringify(accountIds), JSON.stringify(platformIds)])

  useEffect(() => { load() }, [load])
  // Reset to page 1 whenever the filters/sort/search change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1) }, [sortBy, search, currentPeriod, customDateRange?.start, customDateRange?.end, accountType, JSON.stringify(accountIds), JSON.stringify(platformIds)])

  if (detailBrand) {
    const filterBar = (
      <>
        <MultiSelectDropdown label="Types" options={DETECTION_TYPE_OPTS} selected={detectionTypes} onChange={setDetectionTypes} />
        <MultiSelectDropdown label="Placement" options={PLACEMENT_OPTS} selected={placementTiers} onChange={setPlacementTiers} />
        <MultiSelectDropdown label="Category" options={CATEGORY_OPTS} selected={contentCategories} onChange={setContentCategories} />
      </>
    )
    return (
      <CategoryDetailView
        categoryLabel={detailBrand.brand_name}
        subtitle="Posts featuring this brand — reflecting the dashboard filters above."
        cell={{ emv: detailBrand.total_emv, posts: detailBrand.filtered_posts }}
        filters={filterBar}
        backLabel="Back to Brand Value"
        fetchPosts={async () => (await scoutService.getOwnedBrandPosts(customerId, detailBrand.id, { ...filterOpts, detectionTypes, placementTiers, contentCategories })).data ?? []}
        fetchPostBrands={async (post) => post.post_id ? (await scoutService.getPostBrands(customerId, post.post_id)).data ?? [] : []}
        thumbnailUrl={(post) => post.post_id && post.thumbnail_blob_path ? scoutService.getThumbnailUrl(post.post_id, customerId) : null}
        onBack={() => setDetailBrand(null)}
      />
    )
  }

  if (curating) {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <button
            onClick={() => { setCurating(false); void load() }}
            className="mt-1 text-gray-400 hover:text-gray-700"
            aria-label="Back to Brand Value"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Manage brands</h2>
            <p className="text-sm text-gray-500">
              Combine different spellings of the same brand, rename what a brand is called, and
              undo anything — including merges the system applied automatically.
            </p>
          </div>
        </div>
        <BrandCurationPanel customerId={customerId} context="customer" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Brand Value</h2>
          <p className="text-sm text-gray-500">Brands detected in your content, ranked by EMV — reflecting the filters above.</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <label className="inline-flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none"
                 title="Your own brands are detected in your content but left out of these totals by default">
            <input
              type="checkbox"
              checked={includeSelf}
              onChange={e => { setIncludeSelf(e.target.checked); setPage(1) }}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Include our own brands
          </label>
          <button
            onClick={() => setCurating(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            <Merge className="w-4 h-4" />Manage brands
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          label="Total EMV"
          value={loading ? '—' : fmtEMV(summary?.total_emv ?? 0)}
          icon={DollarSign}
          colorScheme="emerald"
          tooltip="Each post is counted once, however many brands appear in it. Per-brand figures below credit a post's full EMV to every brand detected in it, so they overlap and will add up to more than this total."
        />
        <KPICard
          label="Unique Brands"
          value={loading ? '—' : fmtNum(summary?.unique_brands ?? 0)}
          icon={Package}
          colorScheme="blue"
          tooltip="Distinct brands matching the current filters. Names merged together count once, and brands marked as your own are excluded unless you include them."
        />
        <KPICard
          label="Posts"
          value={loading ? '—' : fmtNum(summary?.total_posts ?? 0)}
          icon={BarChart3}
          colorScheme="purple"
          tooltip="Posts with at least one brand detected, counted once each."
        />
        <KPICard
          label="Avg EMV / Post"
          value={loading ? '—' : fmtEMV(summary?.avg_emv ?? 0)}
          icon={Layers}
          colorScheme="amber"
          tooltip="Total EMV divided by posts, both counted once per post."
        />
      </div>

      <div className="flex items-start gap-2 text-xs text-gray-600 bg-blue-50/60 border border-blue-100 rounded-lg px-3 py-2">
        <Info className="h-3.5 w-3.5 text-blue-500 mt-0.5 shrink-0" />
        <span>
          A post's full EMV is credited to <strong>every</strong> brand detected in it, so the
          per-brand figures below overlap and add up to more than Total EMV, which counts each
          post once. Both are correct.
        </span>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search brands"
            className="pl-8 pr-8 py-1.5 text-xs font-medium border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white hover:bg-gray-50 transition-colors shadow-sm w-full min-w-[300px]"
          />
          {search && (
            <button type="button" onClick={() => setSearch('')} title="Clear" className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1.5 text-xs">
          <span className="text-gray-500">Sort by</span>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white hover:bg-gray-50 transition-colors shadow-sm">
            {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        {loading ? (
          <div className="flex items-center justify-center min-h-[280px]"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
        ) : error ? (
          <div className="min-h-[200px] flex items-center justify-center text-sm text-red-600">{error}</div>
        ) : brands.length === 0 ? (
          <div className="min-h-[200px] flex items-center justify-center text-sm text-gray-400">No brands detected for the selected filters.</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {brands.map((b) => {
                const st = brandStyle(b.brand_name)
                const initial = (b.brand_name || '?').trim().charAt(0).toUpperCase()
                return (
                <div key={b.id} onClick={() => openBrand(b)} className="group cursor-pointer rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-0.5 hover:border-gray-300 transition-all duration-300">
                  <div className={`h-1 ${st.bar}`} />
                  <div className="p-4">
                    <div className="flex items-start gap-2.5 mb-3">
                      <div className={`h-9 w-9 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 ${st.avatar}`}>{initial}</div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-semibold text-gray-900 truncate leading-tight" title={b.brand_name}>{b.brand_name}</h3>
                        {b.category && (
                          <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] text-gray-500">
                            <Tag className="h-2.5 w-2.5" />{b.category}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-2xl font-bold text-emerald-600 leading-none">{fmtEMV(b.total_emv ?? 0)}</div>
                    <div className="mt-1 text-[9px] font-semibold uppercase tracking-wider text-gray-400">Total EMV</div>
                    <div className="mt-3 pt-2.5 border-t border-gray-100 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-medium text-gray-600">
                      <span className="flex items-center gap-1"><BarChart3 className="h-3 w-3 text-gray-400" />{b.filtered_posts ?? 0} posts</span>
                      <span className="flex items-center gap-1"><Eye className="h-3 w-3 text-purple-500" />{fmtNum(b.total_views ?? 0)}</span>
                      <span className="flex items-center gap-1"><Heart className="h-3 w-3 text-rose-500" />{fmtNum(b.total_likes ?? 0)}</span>
                      <span className="flex items-center gap-1"><MessageCircle className="h-3 w-3 text-blue-500" />{fmtNum(b.total_comments ?? 0)}</span>
                      <span className="flex items-center gap-1"><Share2 className="h-3 w-3 text-green-500" />{fmtNum(b.total_shares ?? 0)}</span>
                    </div>
                    {b.account_usernames?.length > 0 && (
                      <div className="mt-1.5 text-[10px] text-gray-400 truncate" title={b.account_usernames.join(', ')}>
                        {b.account_usernames.slice(0, 3).map((u) => `@${u}`).join(', ')}{b.account_usernames.length > 3 ? ` +${b.account_usernames.length - 3}` : ''}
                      </div>
                    )}
                  </div>
                </div>
                )
              })}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-4 text-xs">
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="p-1.5 rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50"><ChevronLeft className="h-4 w-4" /></button>
                <span className="text-gray-600">Page {page} of {totalPages}</span>
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="p-1.5 rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50"><ChevronRight className="h-4 w-4" /></button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default BrandValuePanel
